import {ReactElement, useEffect, useRef, useState} from "react";
import makeStyles from "@mui/styles/makeStyles";
import {useReactFlow, useViewport} from "react-flow-renderer";

const useStyles = makeStyles(() => ({
    content: {
        width: "100%",
        height: "100%"
    },
}));

const scrollRatio = {
    x: 0.5,
    y: 0.5
};
const animationTime = 50;

interface IGoal {
    from: IViewport
    to: IViewport
    start: number
}
interface IViewport {
    x: number,
    y: number,
    zoom: number
}
interface ICursorPan {
    children: ReactElement | ReactElement[]
}
export default function CursorPan({children}: ICursorPan) {
    const classes = useStyles();
    const [goal, setGoal] = useState<IGoal>();
    const screenRef = useRef<HTMLDivElement>(null);
    const {x, y, zoom} = useViewport();
    const {setViewport} = useReactFlow();

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            const updateGoal = (toX:number, toY:number) => {
                setGoal(goal => {
                    return {
                        start: goal?.start ?? Date.now(),
                        from: {x: goal?.from.x ?? x, y: goal?.from.y || y, zoom},
                        to: {x: toX, y: toY, zoom}
                    }
                });
            }

            switch(event.key) {
                case "ArrowLeft":
                    updateGoal(x + screenRef.current!.clientWidth * zoom * scrollRatio.x, y);
                    break;
                case "ArrowRight":
                    updateGoal(x - screenRef.current!.clientWidth * zoom * scrollRatio.x, y);
                    break;
                case "ArrowUp":
                    updateGoal(x, y + screenRef.current!.clientHeight * scrollRatio.y * zoom);
                    break;
                case "ArrowDown":
                    updateGoal(x, y - screenRef.current!.clientHeight * scrollRatio.y * zoom);
                    break;
            }
        }

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [setGoal, x, y, zoom]);
    useEffect(() => {
        if (goal) {
            const interval = setInterval(() => {
                const delta = Date.now() - goal.start;
                const proportion = Math.min(1, delta / animationTime);

                if (proportion < 1) {
                    const targetX = goal.from.x + proportion * (goal.to.x - goal.from.x);
                    const targetY = goal.from.y + proportion * (goal.to.y - goal.from.y);

                    setViewport({x: targetX, y: targetY, zoom: goal.to.zoom});
                } else {
                    clearInterval(interval);
                    setViewport({x: goal.to.x, y: goal.to.y, zoom: goal.to.zoom});
                    setGoal(undefined);
                }
            }, 10);
        }
    }, [goal, setGoal, setViewport]);
    return (
        <div className={classes.content} ref={screenRef} tabIndex={0}>
            {children}
        </div>
    );
}